import React from 'react';
import { WebsiteDesignProps } from './types';
import { Leaf, Droplets, Wind } from 'lucide-react';
import { Button } from "@/components/ui/button";

const EnvironmentalScience: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Institute for Earth Sciences";

    return (
        <div className="bg-[#1a2f23] min-h-screen font-sans text-white">
            <nav className="border-b border-white/10 px-8 py-5 flex justify-between items-center">
                <div className="flex items-center gap-2 text-xl font-bold tracking-tight">
                    <Leaf className="text-[#8bc34a]" /> {schoolName}
                </div>
                <div className="hidden md:flex gap-8 text-sm font-medium text-white/60">
                    <a href="#" className="hover:text-[#8bc34a] transition-colors">Research</a>
                    <a href="#" className="hover:text-[#8bc34a] transition-colors">Programs</a>
                    <a href="#" className="hover:text-[#8bc34a] transition-colors">Fieldwork</a>
                </div>
                <Button className="bg-[#8bc34a] text-[#1a2f23] hover:bg-[#9ccc65] font-bold rounded-full px-6">
                    Apply
                </Button>
            </nav>

            <header className="relative py-24 px-8">
                {/* Abstract Map */}
                <div className="absolute inset-0 opacity-10">
                    <svg viewBox="0 0 800 600" className="w-full h-full">
                        <path d="M100,200 Q200,100 300,150 T500,120 T700,200" fill="none" stroke="#8bc34a" strokeWidth="2"/>
                        <path d="M50,350 Q150,300 250,350 T450,320 T650,380" fill="none" stroke="#8bc34a" strokeWidth="2"/>
                        <path d="M150,500 Q250,450 350,480 T550,450 T750,500" fill="none" stroke="#8bc34a" strokeWidth="2"/>
                        <circle cx="300" cy="150" r="8" fill="#8bc34a"/>
                        <circle cx="500" cy="320" r="8" fill="#8bc34a"/>
                        <circle cx="200" cy="480" r="8" fill="#8bc34a"/>
                    </svg>
                </div>

                <div className="container mx-auto relative z-10 max-w-4xl text-center">
                    <div className="inline-block border border-[#8bc34a]/30 bg-[#8bc34a]/10 text-[#8bc34a] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-8">
                        Climate Action Research
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                        Protect Our <br/> <span className="text-[#8bc34a]">Planet.</span>
                    </h1>
                    <p className="text-xl text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
                        Cutting-edge research and education addressing the most pressing environmental challenges of our time.
                    </p>
                    <div className="flex justify-center gap-6">
                        <Button className="bg-[#8bc34a] text-[#1a2f23] hover:bg-[#9ccc65] font-bold uppercase tracking-wider px-10 h-14 rounded-lg">
                            Graduate Programs
                        </Button>
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wider px-10 h-14 rounded-lg">
                            Research Centers
                        </Button>
                    </div>
                </div>
            </header>

            <section className="bg-[#0f1f16] py-20 border-t border-white/10">
                <div className="container mx-auto px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Leaf, title: "Ecology", desc: "Biodiversity conservation and ecosystem dynamics." },
                        { icon: Droplets, title: "Hydrology", desc: "Water resource management and marine science." },
                        { icon: Wind, title: "Atmospheric", desc: "Climate modeling and air quality research." },
                    ].map((item, i) => (
                        <div key={i} className="p-8 border border-white/10 rounded-lg hover:border-[#8bc34a]/50 transition-colors group">
                            <div className="w-14 h-14 rounded-full bg-[#8bc34a]/10 flex items-center justify-center mb-6 group-hover:bg-[#8bc34a]/20 transition-colors">
                                <item.icon className="text-[#8bc34a]" size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                            <p className="text-white/50 leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default EnvironmentalScience;
